import type { MoneyV2 } from "types/storefront.types";
import type { ProductCommonFieldsFragment } from "types/storefront.generated";

import type { KlaviyoItem, KlaviyoViewedProductItem } from "types/klaviyo";

import type { ElasticProduct } from "@usereactify/search";

import type { Site } from "./sites";

/**
 * Generate an "item" used in Klaviyo events.
 * @see https://developers.klaviyo.com/en/docs/custom_event_tracking
 */
export class KlaviyoItemResolver {
  readonly site: Site;

  constructor({ site }: { site: Site }) {
    this.site = site;
  }

  /**
   * Resolve item from the given Shopify product fragment.
   * Prices are not pulled directly from the fragment as their calculations can be more complex.
   */
  resolveFromShopifyProduct({
    price,
    shopifyProduct,
  }: {
    price: MoneyV2;
    compareAtPrice?: MoneyV2 | null;
    shopifyProduct: ProductCommonFieldsFragment;
  }): KlaviyoItem {
    return {
      title: shopifyProduct.title,
      handle: shopifyProduct.handle,
      /**
       * Only the ac_exclude tag is included here.
       * It excludes products from the abandoned cart emails.
       */
      tags: shopifyProduct.tags.filter((tag) => ["ac_exclude"].includes(tag)),
      price: Number(price.amount),
      style: shopifyProduct.styleNameMetafield?.value,
      category: shopifyProduct.classMetafield?.value,
      category2: shopifyProduct.categoryMetafield?.value,
      category3: shopifyProduct.subcategoryMetafield?.value,
      category4: shopifyProduct.statusMetafield?.value,
    };
  }

  /**
   * Resolve payload for the "Viewed Product" event from the given Shopify
   * product fragment.
   */
  resolveViewedProductItem({
    price,
    compareAtPrice,
    shopifyProduct,
  }: {
    price: MoneyV2;
    compareAtPrice?: MoneyV2 | null;
    shopifyProduct: ProductCommonFieldsFragment;
  }): KlaviyoViewedProductItem {
    return {
      ProductName: shopifyProduct.title,
      ProductID: shopifyProduct.id,
      Categories: [],
      ImageURL: shopifyProduct.featuredImage?.url ?? null,
      URL: `https://baredfootwear.com/products/${shopifyProduct.handle}`,
      Price: price.amount,
      CompareAtPrice: compareAtPrice?.amount ?? null,
    };
  }

  /**
   * Resolve item from the given Reactify product.
   */
  resolveFromReactifyProduct({
    reactifyProduct,
  }: {
    reactifyProduct: ElasticProduct;
  }): KlaviyoItem {
    return {
      title: reactifyProduct.title,
      handle: reactifyProduct.handle,
      /**
       * Only the ac_exclude tag is included here.
       * It excludes products from the abandoned cart emails.
       */
      tags: (reactifyProduct.tags ?? []).filter((tag) =>
        ["ac_exclude"].includes(tag),
      ),
      price:
        reactifyProduct.presentment_price_ranges?.max_variant_price?.find(
          (price) => price.currency_code === this.site.currencyCode,
        )?.amount ??
        reactifyProduct.variants?.[0].price ??
        0,
      style: reactifyProduct.metafields?.find(
        (metafield) => metafield.key === "netsuite_style_name",
      )?.value,
      category: reactifyProduct.metafields?.find(
        (metafield) => metafield.key === "netsuite_class",
      )?.value,
      category2: reactifyProduct.metafields?.find(
        (metafield) => metafield.key === "netsuite_category",
      )?.value,
      category3: reactifyProduct.metafields?.find(
        (metafield) => metafield.key === "netsuite_sub_category",
      )?.value,
      category4: reactifyProduct.metafields?.find(
        (metafield) => metafield.key === "netsuite_status",
      )?.value,
    };
  }
}
